import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import styled from 'styled-components';
import { Eye, EyeOff, Save, User, ArrowLeft } from 'lucide-react';
import { api } from '../services/api';

interface ProfileFormData {
  name: string;
  email: string;
  phone: string;
  cnpj: string;
  company_name?: string;
  current_password?: string;
  new_password?: string;
  new_password_confirmation?: string;
}

interface SmtpSettingsForm {
  smtp_host: string;
  smtp_port: string;
  smtp_username: string;
  smtp_password: string;
  smtp_encryption: 'tls' | 'ssl' | 'none';
  from_address: string;
  from_name: string;
  is_active: boolean;
  has_password: boolean;
}

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem;
`;

const Header = styled.div`
  max-width: 800px;
  margin: 0 auto 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  color: #495057;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #00d4aa;
    color: #00d4aa;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Card = styled.div`
  max-width: 800px;
  margin: 0 auto 24px;
  background: white;
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  color: #333;
  margin: 0 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #f0f0f0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  position: relative;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid ${props => props.$hasError ? '#dc3545' : '#e9ecef'};
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }

  &:disabled {
    background: #f8f9fa;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const LogoPreview = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  object-fit: contain;
  background: #f8f9fa;
`;

const LogoPlaceholder = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 12px;
  border: 1px dashed #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 0.875rem;
  background: #fafafa;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 1rem;
  top: 2.5rem;
  color: #adb5bd;
  transition: color 0.2s ease;

  &:hover {
    color: #495057;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 1.5rem;
  border-top: 2px solid #f0f0f0;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: #00d4aa;
  color: white;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #00b894;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 212, 170, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const InfoText = styled.p`
  font-size: 0.875rem;
  color: #6c757d;
  margin: 0 0 1rem 0;
  font-style: italic;
`;

const ToggleRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  color: #495057;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #00d4aa;
`;

export const ProfilePage: React.FC = () => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [smtpSettings, setSmtpSettings] = useState<SmtpSettingsForm>({
    smtp_host: '',
    smtp_port: '',
    smtp_username: '',
    smtp_password: '',
    smtp_encryption: 'tls',
    from_address: '',
    from_name: '',
    is_active: false,
    has_password: false,
  });
  const [smtpAllowed, setSmtpAllowed] = useState(false);
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);

  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm<ProfileFormData>();
  const newPassword = watch('new_password');

  useEffect(() => {
    if (user) {
      // Formatar telefone
      const phone = user.phone || '';
      const formattedPhone = formatPhone(phone);

      // Formatar CPF/CNPJ
      const cnpj = user.cnpj || '';
      const formattedCnpj = formatCPFCNPJ(cnpj);

      reset({
        name: user.name || '',
        email: user.email || '',
        phone: formattedPhone,
        cnpj: formattedCnpj,
        company_name: user.company_name || ''
      });
      setLogoPreview(user.company?.logo_url || null);
      setLogoFile(null);
    }
  }, [user, reset]);

  useEffect(() => {
    const loadSmtpSettings = async () => {
      setSmtpLoading(true);
      try {
        const planResponse = await api.get('/dashboard/plan-usage');
        if (planResponse.data?.success) {
          const plan = planResponse.data?.data?.plan;
          const allowed = !!plan?.has_custom_smtp;
          setSmtpAllowed(allowed);

          if (allowed) {
            const response = await api.get('/crm/email-settings');
            if (response.data?.success && response.data?.data) {
              const data = response.data.data;
              setSmtpSettings(prev => ({
                ...prev,
                smtp_host: data.smtp_host || '',
                smtp_port: data.smtp_port ? String(data.smtp_port) : '',
                smtp_username: data.smtp_username || '',
                smtp_password: '',
                smtp_encryption: data.smtp_encryption || 'tls',
                from_address: data.from_address || '',
                from_name: data.from_name || '',
                is_active: !!data.is_active,
                has_password: !!data.has_password,
              }));
            }
          }
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Erro ao carregar SMTP do CRM.';
        showError(errorMessage);
      } finally {
        setSmtpLoading(false);
      }
    };

    loadSmtpSettings();
  }, [showError]);

  // Função para formatar CPF: 000.000.000-00 ou CNPJ: 00.000.000/0000-00
  const formatCPFCNPJ = (value: string) => {
    const numbers = value.replace(/\D/g, '');

    // CPF (11 dígitos)
    if (numbers.length <= 11) {
      if (numbers.length <= 3) return numbers;
      if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
      if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
      return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
    }

    // CNPJ (14 dígitos)
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    if (numbers.length <= 8) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
    if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
  };

  const handleCPFCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPFCNPJ(e.target.value);
    setValue('cnpj', formatted);
  };

  // Função para formatar telefone: (00) 00000-0000
  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setValue('phone', formatted);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);

    if (!file) {
      setLogoPreview(user?.company?.logo_url || null);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const onSubmit = async (data: ProfileFormData) => {
    setIsLoading(true);

    try {
      const token = localStorage.getItem('auth_token');

      let response;

      if (logoFile) {
        const formData = new FormData();
        formData.append('name', data.name);
        formData.append('email', data.email);
        formData.append('phone', data.phone);
        formData.append('cnpj', data.cnpj);
        if (data.company_name) {
          formData.append('company_name', data.company_name);
        }
        formData.append('logo', logoFile);

        if (changePassword && data.new_password) {
          formData.append('current_password', data.current_password || '');
          formData.append('new_password', data.new_password);
          formData.append('new_password_confirmation', data.new_password_confirmation || '');
        }

        formData.append('_method', 'PUT');

        response = await api.post('/auth/profile', formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        const payload: any = {
          name: data.name,
          email: data.email,
          phone: data.phone,
          cnpj: data.cnpj,
          company_name: data.company_name
        };

        // Adicionar senhas apenas se estiver alterando
        if (changePassword && data.new_password) {
          payload.current_password = data.current_password;
          payload.new_password = data.new_password;
          payload.new_password_confirmation = data.new_password_confirmation;
        }

        response = await api.put('/auth/profile', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response.data.success) {
        showSuccess('Perfil atualizado com sucesso!');
        setChangePassword(false);
        setLogoFile(null);
        if (response.data.data?.user) {
          localStorage.setItem('auth_user', JSON.stringify({ user: response.data.data.user }));
          setLogoPreview(response.data.data.user.company?.logo_url || null);
        }
        // Limpar campos de senha
        setValue('current_password', '');
        setValue('new_password', '');
        setValue('new_password_confirmation', '');
      }

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao atualizar perfil';
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSmtpSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!smtpAllowed) {
      showError('Seu plano nao permite SMTP personalizado.');
      return;
    }

    setSmtpSaving(true);
    try {
      const payload: any = {
        smtp_host: smtpSettings.smtp_host || null,
        smtp_port: smtpSettings.smtp_port ? Number(smtpSettings.smtp_port) : null,
        smtp_username: smtpSettings.smtp_username || null,
        smtp_encryption: smtpSettings.smtp_encryption || 'none',
        from_address: smtpSettings.from_address || null,
        from_name: smtpSettings.from_name || null,
        is_active: smtpSettings.is_active,
      };

      if (smtpSettings.smtp_password) {
        payload.smtp_password = smtpSettings.smtp_password;
      }

      const response = await api.put('/crm/email-settings', payload);
      if (response.data?.success) {
        showSuccess('SMTP do CRM atualizado com sucesso!');
        setSmtpSettings(prev => ({
          ...prev,
          smtp_password: '',
          has_password: prev.has_password || !!smtpSettings.smtp_password,
        }));
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao salvar SMTP do CRM.';
      showError(errorMessage);
    } finally {
      setSmtpSaving(false);
    }
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={20} />
          Voltar
        </BackButton>
        <Title>
          <User size={32} />
          Meu Perfil
        </Title>
      </Header>

      <Card>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Section>
            <SectionTitle>Informações Pessoais</SectionTitle>

            <InputGroup>
              <Label>Nome Completo *</Label>
              <Input
                type="text"
                $hasError={!!errors.name}
                {...register('name', {
                  required: 'Nome é obrigatório',
                  minLength: { value: 2, message: 'Nome deve ter pelo menos 2 caracteres' }
                })}
              />
              {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
            </InputGroup>

            <Row>
              <InputGroup>
                <Label>Email *</Label>
                <Input
                  type="email"
                  $hasError={!!errors.email}
                  {...register('email', {
                    required: 'Email é obrigatório',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Email inválido'
                    }
                  })}
                />
                {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
              </InputGroup>

              <InputGroup>
                <Label>Telefone *</Label>
                <Input
                  type="tel"
                  maxLength={15}
                  $hasError={!!errors.phone}
                  {...register('phone', {
                    required: 'Telefone é obrigatório',
                    pattern: {
                      value: /^\(\d{2}\) \d{5}-\d{4}$/,
                      message: 'Telefone inválido'
                    }
                  })}
                  onChange={handlePhoneChange}
                />
                {errors.phone && <ErrorMessage>{errors.phone.message}</ErrorMessage>}
              </InputGroup>
            </Row>

            <Row>
              <InputGroup>
                <Label>CPF ou CNPJ *</Label>
                <Input
                  type="text"
                  maxLength={18}
                  $hasError={!!errors.cnpj}
                  {...register('cnpj', {
                    required: 'CPF ou CNPJ é obrigatório',
                    validate: (value) => {
                      const numbers = value.replace(/\D/g, '');
                      if (numbers.length === 11) {
                        return /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value) || 'CPF inválido';
                      } else if (numbers.length === 14) {
                        return /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(value) || 'CNPJ inválido';
                      } else {
                        return 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos';
                      }
                    }
                  })}
                  onChange={handleCPFCNPJChange}
                />
                {errors.cnpj && <ErrorMessage>{errors.cnpj.message}</ErrorMessage>}
              </InputGroup>

              <InputGroup>
                <Label>Nome da Empresa</Label>
                <Input
                  type="text"
                  {...register('company_name')}
                />
              </InputGroup>
            </Row>

            <Row>
              <InputGroup>
                <Label>Logo da Empresa</Label>
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                  onChange={handleLogoChange}
                />
              </InputGroup>
              <InputGroup>
                <Label>Pré-visualização</Label>
                {logoPreview ? (
                  <LogoPreview src={logoPreview} alt="Logo da empresa" />
                ) : (
                  <LogoPlaceholder>Nenhum logo</LogoPlaceholder>
                )}
              </InputGroup>
            </Row>
          </Section>

          <Section>
            <SectionTitle>Alterar Senha</SectionTitle>
            <InfoText>
              Deixe em branco se não deseja alterar a senha
            </InfoText>

            {!changePassword ? (
              <Button
                type="button"
                onClick={() => setChangePassword(true)}
                style={{ width: 'fit-content' }}
              >
                Alterar Senha
              </Button>
            ) : (
              <>
                <InputGroup>
                  <Label>Senha Atual *</Label>
                  <Input
                    type={showCurrentPassword ? 'text' : 'password'}
                    $hasError={!!errors.current_password}
                    {...register('current_password', {
                      required: changePassword ? 'Senha atual é obrigatória' : false
                    })}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </PasswordToggle>
                  {errors.current_password && <ErrorMessage>{errors.current_password.message}</ErrorMessage>}
                </InputGroup>

                <Row>
                  <InputGroup>
                    <Label>Nova Senha *</Label>
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      $hasError={!!errors.new_password}
                      {...register('new_password', {
                        required: changePassword ? 'Nova senha é obrigatória' : false,
                        minLength: { value: 8, message: 'Senha deve ter pelo menos 8 caracteres' }
                      })}
                    />
                    <PasswordToggle
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </PasswordToggle>
                    {errors.new_password && <ErrorMessage>{errors.new_password.message}</ErrorMessage>}
                  </InputGroup>

                  <InputGroup>
                    <Label>Confirmar Nova Senha *</Label>
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      $hasError={!!errors.new_password_confirmation}
                      {...register('new_password_confirmation', {
                        required: changePassword ? 'Confirmação de senha é obrigatória' : false,
                        validate: value => !changePassword || value === newPassword || 'Senhas não coincidem'
                      })}
                    />
                    <PasswordToggle
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </PasswordToggle>
                    {errors.new_password_confirmation && <ErrorMessage>{errors.new_password_confirmation.message}</ErrorMessage>}
                  </InputGroup>
                </Row>

                <Button
                  type="button"
                  onClick={() => {
                    setChangePassword(false);
                    setValue('current_password', '');
                    setValue('new_password', '');
                    setValue('new_password_confirmation', '');
                  }}
                  style={{ width: 'fit-content', background: '#6c757d' }}
                >
                  Cancelar Alteração de Senha
                </Button>
              </>
            )}
          </Section>

          <ButtonGroup>
            <Button type="submit" disabled={isLoading}>
              <Save size={20} />
              {isLoading ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </ButtonGroup>
        </Form>
      </Card>

      <Card>
        <Form onSubmit={handleSmtpSubmit}>
          <Section>
            <SectionTitle>SMTP do CRM</SectionTitle>
            {!smtpAllowed && (
              <InfoText>
                Seu plano nao permite SMTP personalizado. Faca upgrade para liberar esse recurso.
              </InfoText>
            )}

            {smtpAllowed && (
              <>
                <Row>
                  <InputGroup>
                    <Label>Servidor SMTP</Label>
                    <Input
                      type="text"
                      value={smtpSettings.smtp_host}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                      disabled={smtpLoading || smtpSaving}
                    />
                  </InputGroup>

                  <InputGroup>
                    <Label>Porta</Label>
                    <Input
                      type="number"
                      value={smtpSettings.smtp_port}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, smtp_port: e.target.value }))}
                      disabled={smtpLoading || smtpSaving}
                    />
                  </InputGroup>
                </Row>

                <Row>
                  <InputGroup>
                    <Label>Usuario SMTP</Label>
                    <Input
                      type="text"
                      value={smtpSettings.smtp_username}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, smtp_username: e.target.value }))}
                      disabled={smtpLoading || smtpSaving}
                    />
                  </InputGroup>

                  <InputGroup>
                    <Label>Senha SMTP</Label>
                    <Input
                      type="password"
                      placeholder={smtpSettings.has_password ? 'Senha ja configurada' : 'Senha SMTP'}
                      value={smtpSettings.smtp_password}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                      disabled={smtpLoading || smtpSaving}
                    />
                    <InfoText>Deixe em branco para manter a senha atual.</InfoText>
                  </InputGroup>
                </Row>

                <Row>
                  <InputGroup>
                    <Label>Email de envio</Label>
                    <Input
                      type="email"
                      value={smtpSettings.from_address}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, from_address: e.target.value }))}
                      disabled={smtpLoading || smtpSaving}
                    />
                  </InputGroup>

                  <InputGroup>
                    <Label>Nome do remetente</Label>
                    <Input
                      type="text"
                      value={smtpSettings.from_name}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, from_name: e.target.value }))}
                      disabled={smtpLoading || smtpSaving}
                    />
                  </InputGroup>
                </Row>

                <Row>
                  <InputGroup>
                    <Label>Criptografia</Label>
                    <Select
                      value={smtpSettings.smtp_encryption}
                      onChange={(e) => setSmtpSettings(prev => ({
                        ...prev,
                        smtp_encryption: e.target.value as SmtpSettingsForm['smtp_encryption'],
                      }))}
                      disabled={smtpLoading || smtpSaving}
                    >
                      <option value="tls">TLS</option>
                      <option value="ssl">SSL</option>
                      <option value="none">Sem criptografia</option>
                    </Select>
                  </InputGroup>

                  <InputGroup>
                    <Label>Ativacao</Label>
                    <ToggleRow>
                      <Checkbox
                        type="checkbox"
                        checked={smtpSettings.is_active}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, is_active: e.target.checked }))}
                        disabled={smtpLoading || smtpSaving}
                      />
                      Usar SMTP personalizado no CRM
                    </ToggleRow>
                  </InputGroup>
                </Row>
              </>
            )}
          </Section>

          <ButtonGroup>
            <Button type="submit" disabled={!smtpAllowed || smtpLoading || smtpSaving}>
              <Save size={18} />
              Salvar SMTP
            </Button>
          </ButtonGroup>
        </Form>
      </Card>
    </Container>
  );
};
